import { useQuery } from '@tanstack/react-query';

import { REPORTING_LE_DATA_SESSIONS_PATH } from '~/consts/reportingLeDataApi';

import type { LeDataSessionSummary } from '~/types/leData';

import { asMantleMode } from '~/utils/leData/engine';
import { getLeDataApiErrorMessage } from '~/utils/leData/getLeDataApiErrorMessage';
import { isInternalLensInstruction } from '~/utils/leData/isInternalLensInstruction';
import { requestWithTimeout } from '~/utils/requestWithTimeout';
import { resolveReportingLeDataUrl } from '~/utils/resolveReportingLeDataUrl';

export const reportingLeDataSessionsQueryKeys = {
  all: ['reporting', 'le-data', 'sessions'] as const,
  lens: (lensId: string) => [...reportingLeDataSessionsQueryKeys.all, 'lens', lensId] as const,
  sidebar: (sidebar: 'lens') => [...reportingLeDataSessionsQueryKeys.all, 'sidebar', sidebar] as const,
};

type SessionsResponse = {
  sessions?: Array<{ sessionId?: string; title?: string; question?: string; updatedAt?: string }>;
};

type RawSessionSummary = {
  sessionId?: string;
  session_id?: string;
  id?: string;
  title?: string;
  name?: string;
  question?: string;
  firstQuestion?: string;
  first_question?: string;
  prompt?: string;
  updatedAt?: string;
  updated_at?: string;
  createdAt?: string;
  created_at?: string;
  pinnedAt?: string;
  pinned_at?: string;
  continuedFrom?: string;
  continued_from?: string;
  humanInitiated?: unknown;
  human_initiated?: unknown;
  engine?: unknown;
  mode?: unknown;
};

function extractRawSessions(data: unknown): Array<RawSessionSummary> {
  if (Array.isArray(data)) {
    return data as Array<RawSessionSummary>;
  }
  if (!data || typeof data !== 'object') {
    return [];
  }

  const root = data as Record<string, unknown>;
  const nestedCandidates = [root, root.data, root.result, root.payload];

  for (const candidate of nestedCandidates) {
    if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) {
      continue;
    }
    const obj = candidate as Record<string, unknown>;
    const list = obj.sessions ?? obj.items ?? obj.results;
    if (Array.isArray(list)) {
      return list as Array<RawSessionSummary>;
    }
  }

  return [];
}

function hasInternalLensInstruction(session: RawSessionSummary): boolean {
  return [
    session.question,
    session.firstQuestion,
    session.first_question,
    session.prompt,
    session.title,
    session.name,
  ].some((text) => typeof text === 'string' && isInternalLensInstruction(text));
}

function normalizeSessions(raw: Array<RawSessionSummary>): Array<LeDataSessionSummary> {
  const isFalseLike = (value: unknown): boolean => value === false || value === 'false' || value === 0;
  return (
    raw
      .map((s): LeDataSessionSummary => {
        // List tags engine; mode only meaningful on mantle.
        const engine = s.engine === 'mantle' ? 'mantle' : s.engine === 'invoke' ? 'invoke' : undefined;
        const mode = engine === 'mantle' ? (asMantleMode(s.mode) ?? undefined) : undefined;
        const pinnedAt =
          (typeof s.pinnedAt === 'string' && s.pinnedAt) || (typeof s.pinned_at === 'string' && s.pinned_at) || '';
        const continuedFrom =
          (typeof s.continuedFrom === 'string' && s.continuedFrom) ||
          (typeof s.continued_from === 'string' && s.continued_from) ||
          '';
        const humanInitiated = isFalseLike(s.humanInitiated) || isFalseLike(s.human_initiated) ? false : true;
        return {
          sessionId:
            (typeof s.sessionId === 'string' && s.sessionId) ||
            (typeof s.session_id === 'string' && s.session_id) ||
            (typeof s.id === 'string' && s.id) ||
            '',
          title:
            (typeof s.title === 'string' && s.title.trim()) ||
            (typeof s.name === 'string' && s.name.trim()) ||
            (typeof s.question === 'string' && s.question.trim()) ||
            (typeof s.firstQuestion === 'string' && s.firstQuestion.trim()) ||
            (typeof s.first_question === 'string' && s.first_question.trim()) ||
            (typeof s.prompt === 'string' && s.prompt.trim()) ||
            'Untitled chat',
          question:
            (typeof s.question === 'string' && s.question.trim()) ||
            (typeof s.firstQuestion === 'string' && s.firstQuestion.trim()) ||
            (typeof s.first_question === 'string' && s.first_question.trim()) ||
            undefined,
          updatedAt:
            (typeof s.updatedAt === 'string' && s.updatedAt) ||
            (typeof s.updated_at === 'string' && s.updated_at) ||
            (typeof s.createdAt === 'string' && s.createdAt) ||
            (typeof s.created_at === 'string' && s.created_at) ||
            new Date().toISOString(),
          ...(engine ? { engine } : {}),
          ...(mode ? { mode } : {}),
          // Spread like engine and mode above: absent on a server that predates
          // them, and absent reads as unpinned, so nothing has to check a version.
          ...(pinnedAt ? { pinnedAt } : {}),
          ...(continuedFrom ? { continuedFrom } : {}),
          humanInitiated,
        };
      })
      // The service applies the same predicate. Keeping it at the boundary as
      // well avoids briefly showing an automated session when an older API node
      // returns a mixed list during a rolling deploy.
      .filter(
        (s, index) => s.sessionId.length > 0 && s.humanInitiated !== false && !hasInternalLensInstruction(raw[index]),
      )
  );
}

/**
 * GET /api/reporting/le-data/sessions — list sessions for the current user.
 *
 * A `lensId` narrows the list to the conversations the service records against that Lens, which
 * is the only place that relationship is held. `sidebar` selects the rail bucket: `lens` is
 * lens_chat and lens_create. The bare list stays chat and slack. lens_build is in neither.
 */
export function useRecentReportingLeDataSessions(enabled = true, lensId?: string | null, sidebar?: 'lens') {
  return useQuery({
    queryKey: lensId
      ? reportingLeDataSessionsQueryKeys.lens(lensId)
      : sidebar
        ? reportingLeDataSessionsQueryKeys.sidebar(sidebar)
        : reportingLeDataSessionsQueryKeys.all,
    queryFn: async ({ signal }) => {
      try {
        const url = resolveReportingLeDataUrl(
          lensId
            ? `${REPORTING_LE_DATA_SESSIONS_PATH}?lensId=${encodeURIComponent(lensId)}`
            : sidebar
              ? `${REPORTING_LE_DATA_SESSIONS_PATH}?sidebar=${sidebar}`
              : REPORTING_LE_DATA_SESSIONS_PATH,
        );
        const data = await requestWithTimeout<SessionsResponse | Array<unknown>>(
          url,
          { signal },
          'Recent threads are taking too long to load. Please retry.',
        );
        return normalizeSessions(extractRawSessions(data));
      } catch (error) {
        throw new Error(getLeDataApiErrorMessage(error), { cause: error });
      }
    },
    enabled,
    staleTime: 60_000,
    retry: false,
  });
}
